import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './categories.css';

const emptyForm = {
  name_en: '',
  name_ar: '',
  description_en: '',
  description_ar: '',
  icon: 'book',
  image_url: '',
};

const iconOptions = {
  Poetry: '✎',
  'Book Club': '▤',
  'Author Talk': '◉',
  Workshop: '♚',
  Festival: '▥',
  Community: '☷',
  book: '▤',
  pen: '✎',
  mic: '◉',
  author: '◉',
  community: '♚',
  festival: '▥',
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [eventCounts, setEventCounts] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) {
      alert(error.message);
      return;
    }

    setCategories(data || []);
    fetchEventCounts(data || []);
  };

  const fetchEventCounts = async (categoryList) => {
    const counts = {};

    for (const category of categoryList) {
      const { count } = await supabase
        .from('event_categories')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', category.id);

      counts[category.id] = count || 0;
    }

    setEventCounts(counts);
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setForm({
      name_en: category.name_en || '',
      name_ar: category.name_ar || '',
      description_en: category.description_en || '',
      description_ar: category.description_ar || '',
      icon: category.icon || 'book',
      image_url: category.image_url || '',
    });

    setEditingId(category.id);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let result;

    if (editingId) {
      result = await supabase.from('categories').update(form).eq('id', editingId);
    } else {
      result = await supabase.from('categories').insert([form]);
    }

    if (result.error) {
      alert(result.error.message);
      return;
    }

    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchCategories();
  };

  const deleteCategory = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this category?'
    );

    if (!confirmDelete) return;

    const { error } = await supabase.from('categories').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchCategories();
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Categories" />

      <main className="dashboard-main">
        <Topbar />

        <section className="categories-content">
          <div className="categories-header">
            <div>
              <h1>Categories Management</h1>
              <p>Organize and manage event categories</p>
            </div>

            <button className="add-category-btn" onClick={openAddModal}>
              + Add Category
            </button>
          </div>

          <div className="categories-grid">
            {categories.map((category, index) => (
              <div className="category-card" key={category.id}>
                <div className="category-top">
                  <div className={`category-icon ${index % 2 === 0 ? 'pink' : 'purple'}`}>
                    {iconOptions[category.icon] || iconOptions[category.name_en] || '▤'}
                  </div>

                  <div className="category-actions">
                    <button onClick={() => openEditModal(category)}>✎</button>
                    <button onClick={() => deleteCategory(category.id)}>🗑</button>
                  </div>
                </div>

                <h3>{category.name_en}</h3>
                <p className="category-ar">{category.name_ar}</p>

                <div className="category-line"></div>

                <div className="category-bottom">
                  <span>Events</span>
                  <strong>{eventCounts[category.id] || 0}</strong>
                </div>
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div className="empty-categories">No categories found.</div>
          )}
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>

      {showModal && (
        <div className="category-modal-overlay">
          <div className="category-modal">
            <div className="category-modal-header">
              <h2>{editingId ? 'Edit Category' : 'Add Category'}</h2>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form className="category-form" onSubmit={handleSubmit}>
              <input
                name="name_en"
                placeholder="Category Name EN"
                value={form.name_en}
                onChange={handleChange}
                required
              />

              <input
                name="name_ar"
                placeholder="Category Name AR"
                value={form.name_ar}
                onChange={handleChange}
                required
              />

              <select name="icon" value={form.icon} onChange={handleChange}>
                <option value="book">Book Club</option>
                <option value="pen">Poetry / Pen</option>
                <option value="mic">Author Talk / Mic</option>
                <option value="community">Workshop / Community</option>
                <option value="festival">Festival</option>
              </select>

              <input
                name="image_url"
                placeholder="Image URL optional"
                value={form.image_url}
                onChange={handleChange}
              />

              <textarea
                name="description_en"
                placeholder="Description EN"
                value={form.description_en}
                onChange={handleChange}
              />

              <textarea
                name="description_ar"
                placeholder="Description AR"
                value={form.description_ar}
                onChange={handleChange}
              />

              <div className="category-modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit">
                  {editingId ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;